import {
  OpenFeature,
  TypedInMemoryProvider,
  type EvaluationContext,
  type InMemoryFlagConfiguration,
} from '@openfeature/web-sdk';

import { LaunchDarklyClientProvider } from '@openfeature/launchdarkly-client-provider';

import { flagDefaults } from './tenantFlags';

export const launchDarklyContextPath = '/api/v1/features/context';
export const startTimeoutSeconds = 5;
const alwaysApply = () => true;

/** Contract for `GET /api/v1/features/context` (DT-10350); matches the LaunchDarkly customer context the backend evaluates server-side. */
export const launchDarklyCustomerKind = 'customer';

export interface LaunchDarklyCustomerContext {
  kind: typeof launchDarklyCustomerKind;
  key: string;
  orgId: string;
  accountId: string;
}

export interface LaunchDarklyInitConfig {
  clientSideId: string;
  hash: string;
  context: LaunchDarklyCustomerContext;
}

export class LaunchDarklyContextValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LaunchDarklyContextValidationError';
  }
}

export function mapLaunchDarklyContextToOpenFeature(
  context: LaunchDarklyCustomerContext
): EvaluationContext {
  if (context.kind !== launchDarklyCustomerKind) {
    throw new LaunchDarklyContextValidationError(
      `Expected context kind "${launchDarklyCustomerKind}", got "${context.kind}"`
    );
  }

  // orgId is the stable targeting key; never fall back to accountId when it's empty.
  if (!context.orgId || context.key !== context.orgId) {
    throw new LaunchDarklyContextValidationError(
      `Expected context.key to equal a nonempty orgId; got key="${context.key}", orgId="${context.orgId}"`
    );
  }

  if (!context.accountId) {
    throw new LaunchDarklyContextValidationError('Expected context.accountId to be nonempty');
  }

  return {
    targetingKey: context.orgId,
    kind: context.kind,
    orgId: context.orgId,
    accountId: context.accountId,
  };
}

type FallbackFlagConfiguration = InMemoryFlagConfiguration<
  Record<string, { on: boolean; off: boolean }>
>;

function buildFallbackConfiguration(): FallbackFlagConfiguration {
  const configuration: FallbackFlagConfiguration = {};

  for (const [flagKey, defaultValue] of Object.entries(flagDefaults)) {
    configuration[flagKey] = {
      variants: { on: true, off: false },
      defaultVariant: defaultValue ? 'on' : 'off',
      disabled: false,
    };
  }

  return configuration;
}

/**
 * Installed synchronously at app boot so consumers never block on the network — real
 * LaunchDarkly evaluations replace these in place once the backend context resolves.
 */
export interface InitializeFallbackProviderOptions {
  shouldApply?: () => boolean;
}

export async function initializeFallbackProvider({
  shouldApply = alwaysApply,
}: InitializeFallbackProviderOptions = {}): Promise<void> {
  if (!shouldApply()) {
    return;
  }

  const provider = new TypedInMemoryProvider(buildFallbackConfiguration());

  if (!shouldApply()) {
    return;
  }

  await OpenFeature.setProviderAndWait(provider);
}

export interface InitializeLaunchDarklyProviderOptions {
  config: LaunchDarklyInitConfig;
  shouldApply?: () => boolean;
}

export async function initializeLaunchDarklyProvider({
  config,
  shouldApply = alwaysApply,
}: InitializeLaunchDarklyProviderOptions): Promise<void> {
  if (!shouldApply()) {
    return;
  }

  const evaluationContext = mapLaunchDarklyContextToOpenFeature(config.context);

  if (!shouldApply()) {
    return;
  }

  await OpenFeature.setContext(evaluationContext);

  const provider = new LaunchDarklyClientProvider(config.clientSideId, {
    streaming: true,
    hash: config.hash,
    initializationTimeout: startTimeoutSeconds,
  });

  if (!shouldApply()) {
    return;
  }

  // Swapping the provider here does not remount React consumers; @openfeature/react-sdk
  // reacts to the provider-changed event and re-renders in place.
  await OpenFeature.setProviderAndWait(provider);
}

export async function resetFeatureFlagProvider(): Promise<void> {
  await OpenFeature.clearProviders();
}

function logFeatureFlagFailure(message: string, error?: unknown): void {
  console.error(`[FeatureFlags] ${message}`, error);
}

export interface InitializeFeatureFlagProviderOptions {
  initConfig?: LaunchDarklyInitConfig;
  contextFetchFailed: boolean;
  shouldApply?: () => boolean;
}

/** Applies a resolved backend config, or falls back to flagDefaults on any failure. */
export async function initializeFeatureFlagProvider({
  initConfig,
  contextFetchFailed,
  shouldApply = alwaysApply,
}: InitializeFeatureFlagProviderOptions): Promise<void> {
  if (!shouldApply()) {
    return;
  }

  if (initConfig) {
    try {
      await initializeLaunchDarklyProvider({ config: initConfig, shouldApply });
      return;
    } catch (error) {
      logFeatureFlagFailure('LaunchDarkly init failed; using flagDefaults', error);
    }
  } else if (contextFetchFailed) {
    logFeatureFlagFailure('Failed to fetch LaunchDarkly context; using flagDefaults');
  }

  await initializeFallbackProvider({ shouldApply });
}
